import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Collapse,
  CircularProgress,
  Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { getAuthToken, getApiBaseUrl } from '../../services/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  query_ga4_analytics: 'Querying analytics',
  get_realtime_ga4: 'Fetching real-time data',
  query_action_log: 'Checking action log',
  query_latest_insights: 'Reading latest insights',
};

const AnalyticsChat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('Thinking...');
  const [streamingText, setStreamingText] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleNewChat = useCallback(() => {
    // Abort triggers the in-flight stream's finally block, which is the
    // single owner of setLoading(false) + setStreamingText(''). Null out
    // the ref so the next handleSend doesn't try to abort a dead controller.
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setSessionId(null);
    setQuery('');
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const handleSend = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed || loading) return;

    const token = getAuthToken();
    if (!token) return;

    const userMsg: Message = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setQuery('');
    setLoading(true);
    setLoadingStatus('Thinking...');
    setStreamingText('');

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/analytics/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: trimmed,
          session_id: sessionId,
        }),
        signal: abortRef.current.signal,
      });

      if (!response.ok || !response.body) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
        ]);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            switch (event.type) {
              case 'delta':
                accumulatedText += event.text;
                setStreamingText(accumulatedText);
                break;
              case 'tool_start':
                setLoadingStatus(TOOL_DISPLAY_NAMES[event.tool] || 'Processing...');
                break;
              case 'tool_done':
                setLoadingStatus('Interpreting results...');
                break;
              case 'done': {
                const finalAnswer = event.query_result?.answer || accumulatedText;
                setStreamingText('');
                if (finalAnswer) {
                  setMessages((prev) => [...prev, { role: 'assistant', content: finalAnswer }]);
                }
                if (event.session_id) {
                  setSessionId(event.session_id);
                }
                break;
              }
              case 'error':
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant', content: event.message || 'An error occurred.' },
                ]);
                // Clear the server-side session only when the backend signals
                // the session is truly dead. Otherwise a session_fatal path
                // (ownership denial, dead session ID) would loop forever on
                // the same rejected session_id.
                if (event.code === 'session_fatal') {
                  setSessionId(null);
                }
                break;
            }
          } catch {
            // skip malformed
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Connection failed. Please try again.' },
      ]);
    } finally {
      setLoading(false);
      setStreamingText('');
    }
  }, [query, loading, sessionId]);

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}>
      <Collapse in={open} unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            width: 400,
            maxHeight: 520,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 1,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: BRAND_COLORS.forestGreen.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Analytics Assistant
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleNewChat}
                disabled={loading || (messages.length === 0 && !sessionId)}
                title="Start a new conversation"
                sx={{ color: 'white' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, minHeight: 300, maxHeight: 380 }}>
            {messages.length === 0 && !loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 4 }}
              >
                Ask me about your website traffic, user behavior, or engagement metrics.
              </Typography>
            )}
            {messages.map((msg, i) => (
              <Box
                key={i}
                data-testid="analytics-chat-message"
                data-role={msg.role}
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor:
                      msg.role === 'user'
                        ? BRAND_COLORS.forestGreen.main
                        : alpha(BRAND_COLORS.forestGreen.main, 0.08),
                    color: msg.role === 'user' ? 'white' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                    {msg.content}
                  </Typography>
                </Box>
              </Box>
            ))}
            {loading && streamingText && (
              <Box sx={{ mb: 1.5 }} data-testid="analytics-chat-streaming">
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(BRAND_COLORS.forestGreen.main, 0.08),
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                    {streamingText}
                  </Typography>
                </Box>
              </Box>
            )}
            {loading && !streamingText && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  {loadingStatus}
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Ask about your analytics..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={loading || !query.trim()}
              size="small"
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>

      {/* Toggle button */}
      {!open && (
        <Chip
          icon={<ChatIcon />}
          label="Analytics Chat"
          onClick={() => setOpen(true)}
          color="primary"
          sx={{
            height: 40,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: 3,
            '&:hover': { boxShadow: 6 },
          }}
        />
      )}
    </Box>
  );
};

export default AnalyticsChat;
